import { useCallback, useMemo } from 'react';

import { useUIDSeed } from '@twilio-paste/core/dist/uid-library';
import { FormControl, FormControlTwoColumn } from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';
import { Select, Option } from '@twilio-paste/core/select';
import { Field, useFormikContext } from 'formik';

import { CommunicationType } from '../../../modules/Apps';
import { InfoLabel } from '../../common/InfoLabel';

import { AppSettingsFormValues } from './AppSettingsForm';

const communicationTypeLabel: Record<CommunicationType, string> = {
  BOTH: 'Both',
  MARKETING: 'Marketing',
  TRANSACTIONAL: 'Transactional',
};

interface EmailChannelSectionProps {
  readOnly?: boolean;
}

export function EmailChannelSection({ readOnly }: EmailChannelSectionProps) {
  const seed = useUIDSeed();
  const {
    values: { communicationType },
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
  } = useFormikContext<AppSettingsFormValues>();

  const handleChangeCommunicationType = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setFieldValue('communicationType', event.target.value);
      setFieldTouched('communicationType', true);
    },
    [setFieldTouched, setFieldValue],
  );

  const { showMarketing, showTransactional } = useMemo(() => {
    return {
      showMarketing: [CommunicationType.Marketing, CommunicationType.Both].includes(
        communicationType,
      ),
      showTransactional: [CommunicationType.Transactional, CommunicationType.Both].includes(
        communicationType,
      ),
    };
  }, [communicationType]);

  return (
    <>
      <FormControl>
        <InfoLabel
          htmlFor={seed('defaultEmailSender')}
          infoText="Please provide the default phone number or short code when sending messages without a defined email."
          required
        >
          Default Email Sender
        </InfoLabel>
        <Field
          name="defaultEmailSender"
          as={Input}
          type="email"
          id={seed('defaultEmailSender')}
          placeholder="Email Sender"
          hasError={touched.defaultEmailSender && !!errors.defaultEmailSender}
          readOnly={readOnly}
        />
        {touched.defaultEmailSender && !!errors.defaultEmailSender && (
          <HelpText variant="error">{errors.defaultEmailSender}</HelpText>
        )}
      </FormControl>
      <FormControl>
        <Label htmlFor={seed('communicationType')} required>
          Email Communication Type
        </Label>
        <Select
          value={communicationType}
          onChange={handleChangeCommunicationType}
          disabled={readOnly}
        >
          {Object.values(CommunicationType).map(value => (
            <Option key={value} value={value}>
              {communicationTypeLabel[value]}
            </Option>
          ))}
        </Select>
      </FormControl>
      <FormControlTwoColumn>
        {showMarketing && (
          <FormControl>
            <Label htmlFor={seed('sendgridPromotionalAsmGroupId')} required>
              Promotional Group ID
            </Label>
            <Field
              name="sendgridPromotionalAsmGroupId"
              as={Input}
              type="number"
              min="1"
              id={seed('sendgridPromotionalAsmGroupId')}
              placeholder="Promotional Group ID"
              hasError={
                touched.sendgridPromotionalAsmGroupId && !!errors.sendgridPromotionalAsmGroupId
              }
              readOnly={readOnly}
            />
            {touched.sendgridPromotionalAsmGroupId && !!errors.sendgridPromotionalAsmGroupId && (
              <HelpText variant="error">{errors.sendgridPromotionalAsmGroupId}</HelpText>
            )}
          </FormControl>
        )}
        {showTransactional && (
          <FormControl>
            <Label htmlFor={seed('sendgridTransactionalAsmGroupId')} required>
              Transactional Group ID
            </Label>
            <Field
              name="sendgridTransactionalAsmGroupId"
              as={Input}
              type="number"
              min="1"
              id={seed('sendgridTransactionalAsmGroupId')}
              placeholder="Transactional Group ID"
              hasError={
                touched.sendgridTransactionalAsmGroupId && !!errors.sendgridTransactionalAsmGroupId
              }
              readOnly={readOnly}
            />
            {touched.sendgridTransactionalAsmGroupId &&
              !!errors.sendgridTransactionalAsmGroupId && (
                <HelpText variant="error">{errors.sendgridTransactionalAsmGroupId}</HelpText>
              )}
          </FormControl>
        )}
      </FormControlTwoColumn>
    </>
  );
}
